import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpIcon } from 'lucide-react';
import { LEVEL_GROUPS, WINE_LEVELS, WINE_PHOTOS, type LevelGroupId } from '../../data/wine';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
const FILTERS: {
  id: LevelGroupId | 'alla';
  label: string;
  range?: string;
}[] = [{
  id: 'alla',
  label: 'Alla'
}, {
  id: 'gott',
  label: 'Något Gott',
  range: 'F–B'
}, {
  id: 'extra',
  label: 'Något Extra',
  range: 'A–AA*'
}, {
  id: 'sjukt',
  label: 'Något Sjukt',
  range: 'AA**–AA***'
}];
export function WineList() {
  const [filter, setFilter] = useState<LevelGroupId | 'alla'>('alla');
  const visibleGroups = LEVEL_GROUPS.filter(group => filter === 'alla' || group.id === filter);
  return <section id="vin-pa-glas" className="w-full px-6 py-24 md:py-32 border-t border-ink/10" aria-labelledby="vinglas-rubrik" data-id="element-236" data-mp="tgbe9vcu0oxj">
      <div className="max-w-6xl mx-auto" data-id="element-237" data-mp="3xq17dayfos">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-10 lg:gap-16 items-end" data-id="element-238" data-mp="3rbjyddayv6bz">
          <div className="max-w-2xl" data-id="element-239" data-mp="9usl918j0pmd">
            <h2 id="vinglas-rubrik" className="font-sans text-5xl md:text-7xl font-medium tracking-[-0.04em] uppercase leading-[0.88] text-ink" data-id="element-240" data-mp="9tr36ct3f2v5">
              Vin på glas
            </h2>
            <p className="mt-6 font-serif italic text-2xl md:text-3xl font-light text-ink/80" data-id="element-241" data-mp="3dbr97mf38y64">
              Alla våra viner på glas kan beställas som helt eller halvt glas.
            </p>
            <div className="mt-8 flex flex-wrap gap-x-10 gap-y-4" data-id="element-242" data-mp="3mdqr0gxxg6a4">
              <div data-id="element-243" data-mp="3iacalp62tnw5">
                <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-ink/45" data-id="element-244" data-mp="3i90xuzunl5sw">
                  Helt glas
                </p>
                <p className="mt-1 font-serif text-3xl font-light text-ink" data-id="element-245" data-mp="xkd9nzng8f31">
                  15 cl
                </p>
              </div>
              <div data-id="element-246" data-mp="3qdsabu9x46z0">
                <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-ink/40" data-id="element-247" data-mp="3qebpj386oh9c">
                  Halvt glas
                </p>
                <p className="mt-1 font-serif text-3xl font-light text-ink/55" data-id="element-248" data-mp="47wpqykhkttv">
                  ca 7,5 cl
                </p>
              </div>
            </div>
          </div>

          <img src={WINE_PHOTOS.pour} alt="Ett glas vin som hälls upp vid bardisken" className="hidden lg:block w-72 aspect-[3/2] object-cover rounded-sm" loading="lazy" data-id="element-249" data-mp="86w419s3y2xz" />
        </div>

        {/* Filters */}
        <div role="group" aria-label="Filtrera vinlistan" className="mt-14 flex flex-wrap gap-2" data-id="element-250" data-mp="3gnh4u3al5pq0">
          {FILTERS.map(item => {
          const isActive = filter === item.id;
          return <button key={item.id} type="button" onClick={() => setFilter(item.id)} aria-pressed={isActive} className={`px-5 py-3 rounded-full border font-sans text-[10px] md:text-[11px] tracking-[0.18em] uppercase transition-colors duration-200 ${isActive ? 'border-ink bg-ink text-white' : 'border-ink/20 text-ink/60 hover:border-ink/50 hover:text-ink'}`} data-id="element-251" data-mp="2szg0l6kfwvge">
                {item.label}
                {item.range && <span className={`ml-2 ${isActive ? 'text-white/55' : 'text-ink/35'}`} data-id="element-252" data-mp="1xqchv5qtpqbu">
                    {item.range}
                  </span>}
              </button>;
        })}
        </div>

        <p className="mt-4 font-sans text-[11px] leading-relaxed text-ink/45" data-id="element-253" data-mp="1or8tpncsukqh">
          Osäker på var du ska börja? Ta Något Gott — friskt, enkelt och
          riktigt gott.
        </p>

        {/* Levels */}
        <div className="mt-14 space-y-16 md:space-y-20" data-id="element-254" data-mp="1etl050qxjj9w">
          {visibleGroups.map(group => {
          const levels = WINE_LEVELS.filter(level => level.group === group.id);
          return <motion.div key={group.id} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-60px'
          }} variants={{
            visible: {
              transition: {
                staggerChildren: 0.05
              }
            }
          }} data-id="element-255" data-mp="354g5y3ayxxtd">
                <motion.div variants={fadeUp} className="flex flex-wrap items-baseline gap-x-4 gap-y-2 pb-4" data-id="element-256" data-mp="304v33hsoh5bd">
                  <h3 className="font-sans text-2xl md:text-3xl font-medium tracking-[-0.02em] uppercase text-ink" data-id="element-257" data-mp="2m6wtsqt7rz2q">
                    {group.title}
                  </h3>
                  <span className="font-sans text-[11px] tracking-[0.2em] uppercase text-ink/45" data-id="element-258" data-mp="keqt1vttsm3b">
                    Nivå {group.range}
                  </span>
                  {group.note && <span className="px-2.5 py-1 border border-ink/25 font-sans text-[9px] tracking-[0.2em] uppercase text-ink/60" data-id="element-259" data-mp="29vw21abph0ym">
                      {group.note}
                    </span>}
                </motion.div>

                {/* Column labels */}
                <div className="hidden sm:flex items-end justify-end gap-10 pb-2" data-id="element-260" data-mp="2ete5il6yenz8">
                  <span className="w-28 text-right font-sans text-[9px] tracking-[0.22em] uppercase text-ink/50" data-id="element-261" data-mp="2sl2cyb69co9v">
                    Helt glas
                  </span>
                  <span className="w-24 text-right font-sans text-[9px] tracking-[0.22em] uppercase text-ink/40" data-id="element-262" data-mp="2nobzlvlu86s5">
                    Halvt glas
                  </span>
                </div>

                <ul className="divide-y divide-ink/10 border-t border-ink/15" data-id="element-263" data-mp="2xl6tg1761j03">
                  {levels.map(level => <motion.li key={level.letter} variants={fadeUp} className="flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-10 py-5" data-id="element-264" data-mp="136e24edbfedy">
                      <div className="flex-1 flex items-baseline gap-4" data-id="element-265" data-mp="1wvd7iasgijn9">
                        <span className="w-16 sm:w-20 shrink-0 font-sans text-2xl md:text-3xl font-medium tracking-[-0.03em] text-ink" data-id="element-266" data-mp="21vho00td7edh">
                          {level.letter}
                        </span>
                        <div data-id="element-267" data-mp="2btxfw607uro1">
                          <p className="font-serif text-lg md:text-xl font-light text-ink" data-id="element-268" data-mp="2bsmay2v1bdvc">
                            {level.name}
                          </p>
                          <p className="mt-0.5 font-sans text-[13px] leading-relaxed text-ink/50" data-id="element-269" data-mp="2klzf60yju1wq">
                            {level.signal}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-baseline gap-6 sm:gap-10 pl-20 sm:pl-0" data-id="element-270" data-mp="2ukybql61i73d">
                        <span className="sm:w-28 text-right font-serif text-2xl md:text-3xl font-light tabular-nums text-ink" data-id="element-271" data-mp="2zi6tikna0943">
                          {level.full} kr
                          <span className="sm:hidden ml-2 font-sans text-[10px] tracking-[0.15em] uppercase text-ink/40" data-id="element-272" data-mp="iwthn3sngntb">
                            helt
                          </span>
                        </span>
                        <span className="sm:w-24 text-right font-sans text-sm md:text-base tabular-nums text-ink/50" data-id="element-273" data-mp="8yvy2tlqjf71">
                          {level.half} kr
                          <span className="sm:hidden ml-2 text-[10px] tracking-[0.15em] uppercase text-ink/35" data-id="element-274" data-mp="16nyx3huohe33">
                            halvt
                          </span>
                        </span>
                      </div>
                    </motion.li>)}
                </ul>
              </motion.div>;
        })}
        </div>

        {/* CTA back to booking */}
        <div className="mt-20 pt-10 border-t border-ink/15 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6" data-id="element-275" data-mp="1pegjz5stxfgj">
          <p className="font-serif italic text-xl md:text-2xl font-light text-ink/70" data-id="element-276" data-mp="sy2otnkna6l9">
            Redo att prova dig fram?
          </p>
          <a href="#boka-bord" className="inline-flex items-center gap-2.5 px-8 py-4 rounded-full bg-ink text-white font-sans text-[10px] md:text-xs tracking-[0.2em] uppercase hover:bg-ink/85 transition-colors duration-200" data-id="element-277" data-mp="xqc8ilv62af3">
            <ArrowUpIcon strokeWidth={1.5} className="w-3.5 h-3.5" data-id="element-278" data-mp="1cm1u4iwfwlja" />
            Boka med vinbricka
          </a>
        </div>
      </div>
    </section>;
}